import clsx from 'clsx';
import styles from './QCStepHeader.module.css';

export interface QCStepHeaderProps {
  programName?: string | null;
  programCode: string;
  websiteUrl?: string | null;
  currentIndex?: number;
  totalPrograms?: number;
  hasPreviousProgram?: boolean;
  hasNextProgram?: boolean;
  onPreviousProgram?: () => void;
  onNextProgram?: () => void;
  isLocked: boolean;
  onToggleLock: () => void;
  lockPending?: boolean;
  timerLabel: string;
  timerRunning: boolean;
  onToggleTimer: () => void;
  onResetTimer: () => void;
  hasUnsavedChanges: boolean;
  onOpenNotes: () => void;
  stepCompleted: boolean;
  onToggleStepComplete: () => void;
  canToggleStepComplete?: boolean;
  flagged?: boolean | null;
  onToggleFlag?: () => void;
  flagEditable?: boolean;
  primaryActionLabel?: string | null;
  primaryActionDisabled?: boolean;
  onPrimaryAction?: (() => void) | null;
  vettingStatus?: 'approved' | 'review' | 'unsuitable' | null;
  onVettingStatusChange?: (status: 'approved' | 'review' | 'unsuitable') => Promise<boolean> | void;
}

export function QCStepHeader({
  programName,
  programCode,
  websiteUrl,
  currentIndex,
  totalPrograms,
  hasPreviousProgram = false,
  hasNextProgram = false,
  onPreviousProgram,
  onNextProgram,
  isLocked,
  onToggleLock,
  lockPending = false,
  timerLabel,
  timerRunning,
  onToggleTimer,
  onResetTimer,
  hasUnsavedChanges,
  onOpenNotes,
  stepCompleted,
  onToggleStepComplete,
  canToggleStepComplete = true,
  flagged = null,
  onToggleFlag,
  flagEditable = true,
  primaryActionLabel,
  primaryActionDisabled = false,
  onPrimaryAction,
  vettingStatus,
  onVettingStatusChange
}: QCStepHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.primaryRow}>
        <div className={styles.programMeta}>
          <div className={styles.programHeading}>
            <div className={styles.programHeadingRow}>
              <h1 className={styles.programName}>{programName ?? 'Program'}</h1>
              <span className={styles.programCode}>{programCode}</span>
            </div>
            <div className={styles.metaRow}>
              {typeof currentIndex === 'number' && typeof totalPrograms === 'number' && (
                <span className={styles.position}>
                  Program {currentIndex + 1} of {totalPrograms}
                </span>
              )}
              {websiteUrl && (
                <a
                  className={styles.visitLink}
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit website
                </a>
              )}
            </div>
          </div>
        </div>
        <div className={styles.programControls}>
          <label className={styles.vettingLabel}>
            Vetting status:
            <select
              className={styles.vettingSelect}
              value={vettingStatus ?? 'review'}
              onChange={(event) => onVettingStatusChange?.(event.target.value as 'approved' | 'review' | 'unsuitable')}
              disabled={!onVettingStatusChange}
              aria-label="Program vetting status"
            >
              <option value="approved">Approved</option>
              <option value="review">Review</option>
              <option value="unsuitable">Unsuitable</option>
            </select>
          </label>
          <button
            type="button"
            className={styles.navButton}
            onClick={onPreviousProgram}
            disabled={!hasPreviousProgram}
            aria-label="Previous program"
          >
            &lt;
          </button>
          <span className={styles.programControlsLabel}>Select Programs</span>
          <button
            type="button"
            className={styles.navButton}
            onClick={onNextProgram}
            disabled={!hasNextProgram}
            aria-label="Next program"
          >
            &gt;
          </button>
        </div>
      </div>

      <div className={styles.secondaryRow}>
        <div className={styles.timerGroup}>
          <span className={clsx(styles.timerLabel, timerRunning && styles.timerRunning)}>{timerLabel}</span>
          <div className={styles.timerButtons}>
            <button type="button" onClick={onToggleTimer} className={styles.timerAction}>
              {timerRunning ? 'Pause' : 'Start'}
            </button>
            <button
              type="button"
              onClick={onResetTimer}
              className={styles.timerAction}
              disabled={timerRunning || timerLabel === '00:00'}
            >
              Reset
            </button>
          </div>
        </div>

        <div className={styles.actionGroup}>
          {hasUnsavedChanges && <span className={styles.unsavedDot} aria-label="Unsaved changes" />}
          <button
            type="button"
            className={clsx(styles.completeToggle, stepCompleted && styles.completeToggleActive)}
            onClick={onToggleStepComplete}
            disabled={!canToggleStepComplete}
          >
            {stepCompleted ? 'Step complete' : 'Mark step complete'}
          </button>
          {typeof flagged === 'boolean' && onToggleFlag && (
            <button
              type="button"
              className={clsx(styles.flagToggleButton, flagged && styles.flagToggleButtonActive)}
              onClick={onToggleFlag}
              disabled={!flagEditable}
            >
              {flagged ? 'Flagged for review' : 'Flag for review'}
            </button>
          )}
          <button type="button" className={styles.notesButton} onClick={onOpenNotes}>
            QC Notes
          </button>
          {primaryActionLabel && onPrimaryAction && (
            <button
              type="button"
              className={styles.runButton}
              onClick={onPrimaryAction}
              disabled={primaryActionDisabled}
            >
              {primaryActionLabel}
            </button>
          )}
          <button
            type="button"
            className={clsx(styles.lockButton, isLocked ? styles.locked : styles.unlocked)}
            onClick={onToggleLock}
            disabled={lockPending}
          >
            {lockPending ? '…' : isLocked ? 'Unlock' : 'Lock'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QCStepHeader;
